import React from 'react';
import { Card, CardContent } from './components/Card';
import { MapPin, Phone, CheckCircle2, Facebook, Clock, Shield, Sparkles, Wind, Droplets } from 'lucide-react';
const btnBase = 'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 disabled:pointer-events-none disabled:opacity-50';
export function App() {
  return <div className="min-h-screen bg-[#111] text-white font-sans scroll-smooth selection:bg-yellow-500/30" data-id="element-0">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-white/10" data-id="element-1">
        <div className="container mx-auto px-4 h-24 flex items-center justify-between" data-id="element-2">
          <img src="https://cdn.magicpatterns.com/uploads/sBy1MKMJVW4qhHULCUJR9i/spades-logo.png" alt="Spades Mobile Detailing" className="h-16 md:h-20 object-contain" data-id="element-3" />
          <div className="hidden md:flex items-center gap-6" data-id="element-4">
            <a href="#services" className="text-sm font-medium text-gray-300 hover:text-yellow-500 transition-colors" data-id="element-5">
              Services
            </a>
            <a href="#gallery" className="text-sm font-medium text-gray-300 hover:text-yellow-500 transition-colors" data-id="element-6">
              Gallery
            </a>
            <div className="flex items-center gap-2 text-yellow-500 font-bold" data-id="element-7">
              <Phone className="w-4 h-4" data-id="element-8" />
              <span data-id="element-9">928-607-9726</span>
            </div>
          </div>
          <a href="tel:+19286079726" className={`${btnBase} h-9 px-4 py-2 text-sm bg-gradient-to-r from-yellow-600 to-yellow-400 hover:from-yellow-500 hover:to-yellow-300 text-black font-bold border-none md:hidden`} data-id="element-10">
            <Phone className="w-4 h-4 mr-2" data-id="element-11" />
            Call
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-4 relative overflow-hidden" data-id="element-12">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1601362840469-51e4d8d58785?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20 mix-blend-luminosity" data-id="element-13"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#111]/50 via-[#111]/80 to-[#111]" data-id="element-14"></div>

        <div className="container mx-auto relative z-10 flex flex-col items-center text-center" data-id="element-15">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-4 py-1.5 text-sm font-medium text-yellow-500" data-id="element-16">
            Mobile Detailing • Flagstaff, AZ
          </span>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-17">
            Premium Mobile Detailing in{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-200" data-id="element-18">
              Flagstaff
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl font-light" data-id="element-19">
            Professional • Convenient • Quality Work
          </p>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-20">
            <a href="tel:+19286079726" className={`${btnBase} bg-gradient-to-r from-yellow-600 to-yellow-400 hover:from-yellow-500 hover:to-yellow-300 text-black font-bold text-lg h-14 px-8`} data-id="element-21">
              <Phone className="w-5 h-5 mr-2" data-id="element-22" />
              Call or Text Now
            </a>
            <a href="#gallery" className={`${btnBase} border border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 h-14 px-8 text-lg bg-transparent`} data-id="element-23">
              View Our Work
            </a>
          </div>

          <div className="mt-12 flex items-center gap-2 text-gray-400 bg-white/5 px-4 py-2 rounded-full border border-white/10" data-id="element-24">
            <MapPin className="w-4 h-4 text-yellow-500" data-id="element-25" />
            <span className="text-sm" data-id="element-26">
              Serving Flagstaff & Surrounding Areas
            </span>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-black" data-id="element-27">
        <div className="container mx-auto px-4" data-id="element-28">
          <div className="text-center mb-16" data-id="element-29">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-30">
              Our Premium Services
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-600 to-yellow-400 mx-auto rounded-full" data-id="element-31"></div>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto" data-id="element-32">
              We bring the highest quality detailing right to your doorstep.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-33">
            {[{
            icon: Sparkles,
            title: 'Deep Interior Cleaning',
            desc: 'Thorough vacuuming, stain removal, and deep cleaning of all surfaces.'
          }, {
            icon: Wind,
            title: 'Steam Cleaning',
            desc: 'Sanitize and refresh upholstery, carpets, and hard-to-reach areas.'
          }, {
            icon: Droplets,
            title: 'Odor Elimination',
            desc: 'Complete removal of stubborn smells, leaving your car fresh.'
          }, {
            icon: Shield,
            title: 'Interior Protection & Shine',
            desc: 'Conditioning and protecting plastics, vinyl, and leather.'
          }, {
            icon: Clock,
            title: 'UV & Environmental Protection',
            desc: 'Shield your interior from sun damage and fading.'
          }, {
            icon: CheckCircle2,
            title: 'Convenient Mobile Service',
            desc: 'We come to your home or office, fully equipped.'
          }].map((service, i) => <Card key={i} className="bg-[#1a1a1a] border-white/5 hover:border-yellow-500/30 transition-colors group" data-id="element-34">
                <CardContent className="p-6" data-id="element-35">
                  <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-4 group-hover:bg-yellow-500/20 transition-colors" data-id="element-36">
                    <service.icon className="w-6 h-6 text-yellow-500" data-id="element-37" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white" data-id="element-38">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-39">
                    {service.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section id="gallery" className="py-20 bg-[#111]" data-id="element-40">
        <div className="container mx-auto px-4" data-id="element-41">
          <div className="text-center mb-16" data-id="element-42">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-43">
              The Spades Difference
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-600 to-yellow-400 mx-auto rounded-full" data-id="element-44"></div>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto" data-id="element-45">
              Real results from our deep interior detailing services.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-46">
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative group" data-id="element-47">
              <img src="https://cdn.magicpatterns.com/uploads/ingLfHDNDCCJHSmdySEmxD/before-after2.jpg" alt="Before and After Detailing" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-48" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-49">
                <p className="text-white font-medium" data-id="element-50">
                  Deep Interior Restoration
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative group" data-id="element-51">
              <img src="https://cdn.magicpatterns.com/uploads/aNYd282JUjyJcs6ucdW2bG/before-after1.jpg" alt="Before and After Detailing" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-52" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-53">
                <p className="text-white font-medium" data-id="element-54">
                  Carpet & Upholstery Revival
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative group" data-id="element-55">
              <img src="https://cdn.magicpatterns.com/uploads/eqPBswFoeNS8NgYhhvXQev/before-after3.jpg" alt="Before and after detailing of a Maserati interior" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-56" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-57">
                <p className="text-white font-medium" data-id="element-58">Luxury Interior Detail</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl relative group" data-id="element-59">
              <img src="https://cdn.magicpatterns.com/uploads/jV7N9yvjVhPHfDc9zFWBJn/before-after4.jpg" alt="Before and after detailing of a Maserati cockpit and seats" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-61">
                <p className="text-white font-medium" data-id="element-62">Cockpit & Seat Revival</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-id="element-63">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-900/40 to-black" data-id="element-64"></div>
        <div className="container mx-auto px-4 relative z-10 text-center" data-id="element-65">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-66">
            Ready to Transform Your Ride?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-67">
            Book your mobile detailing appointment today. We bring the shine to
            you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6" data-id="element-68">
            <div className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-200" data-id="element-69">
              928-607-9726
            </div>
            <span className="text-gray-400 hidden sm:block" data-id="element-70">|</span>
            <a href="sms:+19286079726" className={`${btnBase} bg-gradient-to-r from-yellow-600 to-yellow-400 hover:from-yellow-500 hover:to-yellow-300 text-black font-bold text-lg h-14 px-8 w-full sm:w-auto`} data-id="element-71">
              Call or Text Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 pt-16 pb-8" data-id="element-72">
        <div className="container mx-auto px-4" data-id="element-73">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12" data-id="element-74">
            <div data-id="element-75">
              <img src="https://cdn.magicpatterns.com/uploads/sBy1MKMJVW4qhHULCUJR9i/spades-logo.png" alt="Spades Mobile Detailing" className="h-16 object-contain mb-6" data-id="element-76" />
              <p className="text-gray-400 leading-relaxed" data-id="element-77">
                Premium mobile detailing services delivering professional,
                convenient, and quality work directly to you.
              </p>
            </div>

            <div data-id="element-78">
              <h4 className="text-lg font-bold text-white mb-6" data-id="element-79">
                Contact Info
              </h4>
              <ul className="space-y-4" data-id="element-80">
                <li className="flex items-center gap-3 text-gray-400" data-id="element-81">
                  <Phone className="w-5 h-5 text-yellow-500" data-id="element-82" />
                  <span data-id="element-83">928-607-9726</span>
                </li>
                <li className="flex items-start gap-3 text-gray-400" data-id="element-84">
                  <MapPin className="w-5 h-5 text-yellow-500 shrink-0 mt-1" data-id="element-85" />
                  <span data-id="element-86">
                    Serving Flagstaff &<br data-id="element-87" />
                    Surrounding Areas
                  </span>
                </li>
              </ul>
            </div>

            <div data-id="element-88">
              <h4 className="text-lg font-bold text-white mb-6" data-id="element-89">
                Connect With Us
              </h4>
              <div className="flex gap-4" data-id="element-90">
                {/* TODO: replace href with the real Spades Mobile Detailing Facebook page URL */}
                <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" aria-label="Spades Mobile Detailing on Facebook" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-yellow-500 hover:text-black transition-colors" data-id="element-91">
                  <Facebook className="w-5 h-5" data-id="element-92" />
                </a>
                <a href="https://www.google.com/maps/place/Spades+Mobile+Detailing/@35.1828354,-111.6130335,29080m/data=!3m2!1e3!4b1!4m6!3m5!1s0x2483d94154622e77:0xe5b71d194b5d40fd!8m2!3d35.1828354!4d-111.6130336!16s%2Fg%2F11yx3r8fvn" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-yellow-500 hover:text-black transition-colors" data-id="element-93">
                  <MapPin className="w-5 h-5" data-id="element-94" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center text-sm text-gray-500" data-id="element-95">
            <p data-id="element-96">
              &copy; {new Date().getFullYear()} Spades Mobile Detailing. All
              rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}